// src/pages/dashboard/DashboardPage.tsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import * as api from "../../api/walletApi";
import { Retiro } from "../../types";
import { useAppDispatch } from "../../hooks/redux";
import { fetchRetiros } from "../../store/slices/retirosSlice";

// ── helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(n);

const PIE_COLORS: Record<string, string> = {
  PENDIENTE: "#f98115",
  APROBADO: "#0cbb1d",
  RECHAZADO: "#d60d4c",
  PROCESADO: "#3b82f6",
};

// ── StatCard uniforme ─────────────────────────────────────────────────────────

function StatCard({
  icon,
  label,
  value,
  sub,
  color,
  onClick,
}: {
  icon: string;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
  onClick?: () => void;
}) {
  return (
    <div
      className="stat-card h-100"
      style={{ cursor: onClick ? "pointer" : "default" }}
      onClick={onClick}
    >
      <div className={`stat-icon ${color}`}>
        <i className={`bi ${icon}`} />
      </div>
      <div className="stat-info">
        <div className="stat-label">{label}</div>
        <div className="stat-value" style={{ fontSize: "1.4rem" }}>
          {value}
        </div>
        {sub && <div className="stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

// ── PÁGINA PRINCIPAL ──────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [retiros, setRetiros] = useState<Retiro[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getRetiros(undefined, 0, 200);
        const page = res.content as any;
        const items: Retiro[] = page?.content ?? page?.items ?? [];
        setRetiros(items);
        dispatch(fetchRetiros({}));
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, [dispatch]);

  // ── cálculos ─────────────────────────────────────────────────────────────────

  const stats = {
    pendientes: retiros.filter((r) => r.estado === "PENDIENTE").length,
    aprobados: retiros.filter((r) => r.estado === "APROBADO").length,
    rechazados: retiros.filter((r) => r.estado === "RECHAZADO").length,
    procesados: retiros.filter((r) => r.estado === "PROCESADO").length,
    totalMonto: retiros.reduce((s, r) => s + (r.monto ?? 0), 0),
    montoPendiente: retiros
      .filter((r) => r.estado === "PENDIENTE")
      .reduce((s, r) => s + (r.monto ?? 0), 0),
  };

  const pieData = [
    {
      name: "Pendientes",
      value: stats.pendientes,
      color: PIE_COLORS.PENDIENTE,
    },
    { name: "Aprobados", value: stats.aprobados, color: PIE_COLORS.APROBADO },
    {
      name: "Rechazados",
      value: stats.rechazados,
      color: PIE_COLORS.RECHAZADO,
    },
    {
      name: "Procesados",
      value: stats.procesados,
      color: PIE_COLORS.PROCESADO,
    },
  ].filter((d) => d.value > 0);

  const bancoMap: Record<string, number> = {};
  retiros.forEach((r) => {
    const b = r.banco || "Sin especificar";
    bancoMap[b] = (bancoMap[b] || 0) + (r.monto ?? 0);
  });
  const bancoData = Object.entries(bancoMap)
    .map(([banco, monto]) => ({ banco, monto }))
    .sort((a, b) => b.monto - a.monto)
    .slice(0, 6);

  const ultimosPendientes = retiros
    .filter((r) => r.estado === "PENDIENTE")
    .slice(0, 5);

  const formatDate = (d?: string) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: 300 }}
      >
        <div className="spinner-border" style={{ color: "var(--fg-green)" }} />
      </div>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Page header */}
      <div className="page-header">
        <h1 className="page-title">
          <i
            className="bi bi-grid-1x2-fill me-2"
            style={{ color: "var(--fg-green)", fontSize: "1.2rem" }}
          />
          Dashboard
        </h1>
        <p className="page-subtitle">
          Resumen financiero del módulo Billetera Médicos Veterinarios
          Zooctenistas (MVZ) · Feriagana
        </p>
      </div>

      {/* ── Stats — 6 tarjetas uniformes en 3 columnas desktop ── */}
      <div className="row g-3 mb-4">
        {[
          {
            icon: "bi-hourglass-split",
            label: "Retiros pendientes",
            value: stats.pendientes,
            sub: "Requieren acción",
            color: "orange",
            onClick: () => navigate("/retiros?estado=PENDIENTE"),
          },
          {
            icon: "bi-check-circle-fill",
            label: "Aprobados",
            value: stats.aprobados,
            sub: "Esperan transferencia",
            color: "green",
          },
          {
            icon: "bi-x-circle-fill",
            label: "Rechazados",
            value: stats.rechazados,
            sub: "Saldo devuelto",
            color: "red",
          },
          {
            icon: "bi-bank",
            label: "Procesados",
            value: stats.procesados,
            sub: "Transferidos",
            color: "blue",
          },
          {
            icon: "bi-cash-coin",
            label: "Monto pendiente",
            value: fmt(stats.montoPendiente),
            sub: "Por aprobar",
            color: "orange",
          },
          {
            icon: "bi-graph-up-arrow",
            label: "Total retiros",
            value: fmt(stats.totalMonto),
            sub: "Histórico",
            color: "green-light",
          },
        ].map((card) => (
          <div className="col-12 col-sm-6 col-lg-4" key={card.label}>
            <StatCard {...card} />
          </div>
        ))}
      </div>

      {/* ── Charts ── */}
      <div className="row g-3 mb-4">
        {/* Pie */}
        <div className="col-12 col-lg-5">
          <div className="fg-card h-100">
            <div className="fg-card-header">
              <div className="fg-card-title">
                <i
                  className="bi bi-pie-chart-fill me-2"
                  style={{ color: "var(--fg-green)" }}
                />
                Estado de retiros
              </div>
            </div>
            <div style={{ padding: "16px 0 20px" }}>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => [`${v} retiros`]} />
                    <Legend
                      formatter={(val) => (
                        <span style={{ fontSize: "0.8rem" }}>{val}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-state">
                  <i className="bi bi-pie-chart" />
                  <p>Sin datos</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bar */}
        <div className="col-12 col-lg-7">
          <div className="fg-card h-100">
            <div className="fg-card-header">
              <div className="fg-card-title">
                <i
                  className="bi bi-bar-chart-fill me-2"
                  style={{ color: "var(--fg-green)" }}
                />
                Montos por banco
              </div>
            </div>
            <div style={{ padding: "16px 16px 20px" }}>
              {bancoData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={bancoData}
                    margin={{ top: 4, right: 8, left: 8, bottom: 0 }}
                  >
                    <XAxis
                      dataKey="banco"
                      tick={{ fontSize: 11, fill: "var(--fg-muted)" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "var(--fg-muted)" }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      formatter={(v: any) => [fmt(v), "Monto"]}
                      contentStyle={{ borderRadius: 8, fontSize: "0.82rem" }}
                    />
                    <Bar
                      dataKey="monto"
                      fill="var(--fg-green)"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={48}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-state">
                  <i className="bi bi-bar-chart" />
                  <p>Sin datos de bancos</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Últimos pendientes ── */}
      <div className="fg-card">
        <div className="fg-card-header">
          <div className="fg-card-title">
            <i
              className="bi bi-clock-history me-2"
              style={{ color: "var(--fg-orange)" }}
            />
            Retiros pendientes recientes
          </div>
          <button
            className="btn-fg-outline"
            onClick={() => navigate("/retiros?estado=PENDIENTE")}
          >
            <i className="bi bi-arrow-right" /> Ver todos
          </button>
        </div>
        {ultimosPendientes.length === 0 ? (
          <div className="empty-state">
            <i
              className="bi bi-check-all"
              style={{ color: "var(--fg-green)" }}
            />
            <p>No hay retiros pendientes. ¡Todo al día!</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="fg-table">
              <thead>
                <tr>
                  <th>MVZ ID</th>
                  <th>Monto</th>
                  <th>Banco</th>
                  <th>Titular</th>
                  <th>Fecha</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {ultimosPendientes.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <code style={{ fontSize: "0.75rem" }}>
                        {r.mvzId?.slice(0, 8)}...
                      </code>
                    </td>
                    <td>
                      <span className="money-value">{fmt(r.monto)}</span>
                    </td>
                    <td>{r.banco || "—"}</td>
                    <td>{r.titular || "—"}</td>
                    <td className="text-muted-fg">
                      {formatDate(r.createdDate)}
                    </td>
                    <td>
                      <button
                        className="btn-fg-primary"
                        style={{ padding: "5px 12px", fontSize: "0.78rem" }}
                        onClick={() => navigate("/retiros?estado=PENDIENTE")}
                      >
                        <i className="bi bi-eye" /> Gestionar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
